import * as Sentry from "@sentry/react-native";
import * as SecureStore from "expo-secure-store";
import { StatusBar } from "expo-status-bar";
import * as Updates from "expo-updates";
import {
  AppLocale,
  CalleeLanguage,
  CallSession,
  CallerLanguage,
  CreateCallSessionRequest,
  RealtimeEvent,
  SupportedLanguage,
  TranscriptSegment,
  UserProfile,
  Wallet,
  defaultRatePerMinute,
  demoTopUpAmount,
  minimumCallReserveCredits,
  supportedCallerLanguages,
} from "@nativecall/shared";

const creditsToMinutes = (credits: number, ratePerMinute?: number) => {
  const rate = ratePerMinute && ratePerMinute > 0 ? ratePerMinute : defaultRatePerMinute;
  return Math.floor(credits / rate);
};

// Bumped on every OTA so the TestFlight build shows which update it's running.
// Keep this aligned with the `--message` passed to `eas update`.
const OTA_TAG = "test mode auto-on web";

const buildInfoLabel = (() => {
  const runtime = Updates.runtimeVersion || "dev";
  const formatJst = (d: Date) =>
    new Date(d.getTime() + 9 * 60 * 60 * 1000)
      .toISOString()
      .replace("T", " ")
      .slice(0, 16) + " JST";
  const receivedJst = Updates.createdAt ? formatJst(new Date(Updates.createdAt)) : null;
  const shortId = Updates.updateId ? Updates.updateId.slice(0, 8) : null;
  if (Updates.isEmbeddedLaunch || !receivedJst) {
    return `v${runtime} • bundled • ${OTA_TAG}`;
  }
  return shortId
    ? `v${runtime} • OTA ${shortId} • ${OTA_TAG} • ${receivedJst}`
    : `v${runtime} • OTA • ${OTA_TAG} • ${receivedJst}`;
})();
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Animated,
  KeyboardAvoidingView,
  Linking,
  Modal,
  Platform,
  Pressable,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import {
  RECORDING_ENABLED,
  cancelCallSession,
  connectRealtime,
  createCallSession,
  deleteAccount,
  endCallSession,
  fetchCallRecordings,
  fetchCallSession,
  fetchCallSessions,
  fetchTranscript,
  fulfillPurchase,
  signUp,
  startCallSession,
  topUpWallet,
} from "./src/lib/api";
import {
  createAudioPlayer,
  type AudioPlayer,
} from "expo-audio";

const RINGBACK_SOURCE = require("./assets/sounds/ringback.mp3");
import {
  configurePurchases,
  fetchTopUpPackages,
  purchaseTopUpPackage,
  restorePurchases,
  signOutPurchases,
  type TopUpPackage,
} from "./src/lib/purchases";

const SENTRY_DSN = process.env.EXPO_PUBLIC_SENTRY_DSN ?? "";

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    tracesSampleRate: 1.0,
    debug: __DEV__,
  });
}

const showDemoTopUp =
  process.env.EXPO_PUBLIC_SHOW_DEMO_TOP_UP === "true" || __DEV__;
const isNativePlatform = Platform.OS === "ios" || Platform.OS === "android";
import { getCopy, languageLabels } from "./src/lib/i18n";
import {
  getLegalDocument,
  legalLinks,
  type LegalDocumentKind,
} from "./src/lib/legal";
import {
  BrowserVoiceSession,
  PreparedBrowserVoiceClient,
  prepareBrowserVoiceClient,
} from "./src/lib/webVoiceClient";
import { TranslationDemoCard } from "./src/components/TranslationDemoCard";
import { CallRecordingsModal } from "./src/components/CallRecordingsModal";
import { DevMockCalleeScreen } from "./src/components/DevMockCalleeScreen";
import { TestCallScreen } from "./src/components/TestCallScreen";
import {
  CallTranscriptChat,
  PartialTranscript,
} from "./src/components/CallTranscriptChat";

const USER_STORAGE_KEY = "nativecall_user";
const WALLET_STORAGE_KEY = "nativecall_wallet";
const LAST_DESTINATION_KEY = "nativecall_last_destination";
const TEST_MODE_STORAGE_KEY = "nativecall_test_mode";

const isWeb = Platform.OS === "web";

async function storageSet(key: string, value: string) {
  if (isWeb) {
    window.localStorage.setItem(key, value);
    return;
  }
  await SecureStore.setItemAsync(key, value);
}

async function storageGet(key: string) {
  if (isWeb) {
    return window.localStorage.getItem(key);
  }
  return SecureStore.getItemAsync(key);
}

async function storageDelete(key: string) {
  if (isWeb) {
    window.localStorage.removeItem(key);
    return;
  }
  await SecureStore.deleteItemAsync(key);
}

async function saveUserLocally(user: UserProfile, wallet: Wallet) {
  await storageSet(USER_STORAGE_KEY, JSON.stringify(user));
  await storageSet(WALLET_STORAGE_KEY, JSON.stringify(wallet));
}

async function loadUserLocally(): Promise<{ user: UserProfile; wallet: Wallet } | null> {
  const userStr = await storageGet(USER_STORAGE_KEY);
  const walletStr = await storageGet(WALLET_STORAGE_KEY);
  if (!userStr || !walletStr) return null;
  try {
    return { user: JSON.parse(userStr), wallet: JSON.parse(walletStr) };
  } catch {
    return null;
  }
}

async function clearUserLocally() {
  await storageDelete(USER_STORAGE_KEY);
  await storageDelete(WALLET_STORAGE_KEY);
  await storageDelete(LAST_DESTINATION_KEY);
}

const defaultNumber = "+81312345678";

function hasLiveTranslation(
  callSession: CallSession,
  browserVoiceStatus: string | null,
) {
  return (
    callSession.translationState === "active" ||
    callSession.status === "connected" ||
    callSession.status === "translating" ||
    callSession.status === "degraded" ||
    callSession.pstnLegState === "streaming" ||
    browserVoiceStatus === "remote-audio-ready"
  );
}

function getCallStageLabel(
  copy: ReturnType<typeof getCopy>,
  callSession: CallSession,
  browserVoiceStatus: string | null,
) {
  if (callSession.status === "failed" || callSession.translationState === "failed") {
    return copy.failedState;
  }

  if (callSession.status === "ended" || callSession.translationState === "ended") {
    return copy.endingState;
  }

  if (hasLiveTranslation(callSession, browserVoiceStatus)) {
    return copy.liveTranslationState;
  }

  // Pre-pickup: mirror the iPhone call screen — user only sees Ringing vs
  // Calling. Internal setup labels (preparing mic, connecting translator)
  // are misleading because they can read as "already connected" — ringback
  // is still playing and the callee hasn't picked up yet.
  if (
    callSession.status === "ringing" ||
    callSession.pstnLegState === "ringing" ||
    callSession.providerCallStatus === "ringing" ||
    callSession.providerCallStatus === "in-progress"
  ) {
    return copy.ringingState;
  }

  return copy.callingState;
}

function getCallHint(
  copy: ReturnType<typeof getCopy>,
  callSession: CallSession,
  browserVoiceStatus: string | null,
) {
  if (hasLiveTranslation(callSession, browserVoiceStatus)) {
    return copy.liveCallHint;
  }

  if (callSession.status === "failed" || callSession.translationState === "failed") {
    return copy.genericError;
  }

  return copy.callingHint;
}

function formatDuration(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = Math.floor(totalSeconds % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function getCallDurationLabel(callSession: CallSession, nowMs: number) {
  const anchor = callSession.startedAt ?? callSession.createdAt;
  const elapsedSeconds = Math.max(
    0,
    Math.floor((nowMs - new Date(anchor).getTime()) / 1000),
  );
  return formatDuration(elapsedSeconds);
}

function getCallBadgeLabel(destinationNumber: string) {
  const digits = destinationNumber.replace(/\D/g, "");
  return digits.slice(-2).padStart(2, "0");
}

function isBlockedJapanServiceNumber(value: string) {
  const blockedShortCodes = new Set(["110", "118", "119", "171", "177"]);
  const digits = value.replace(/\D+/g, "");

  if (blockedShortCodes.has(digits)) {
    return true;
  }

  if (digits.startsWith("81")) {
    const withoutCountryCode = digits.slice(2).replace(/^0+/, "");
    return blockedShortCodes.has(withoutCountryCode);
  }

  if (digits.startsWith("0")) {
    return blockedShortCodes.has(digits.replace(/^0+/, ""));
  }

  return false;
}

async function openExternalUrl(url: string) {
  try {
    await Linking.openURL(url);
  } catch {}
}

function isDevCalleeUrlMode() {
  if (Platform.OS !== "web") return false;
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("devCallee") === "1";
  } catch {
    return false;
  }
}

export default function App() {
  if (isDevCalleeUrlMode()) {
    return <DevMockCalleeScreen />;
  }
  // Auto-on for web: the build-info long-press (discovery mechanism on native)
  // isn't visible on the Cloudflare Pages site, so anyone opening the web app is
  // there to test and gets mock mode by default. Native iOS still starts off.
  const [testModeEnabled, setTestModeEnabled] = useState(isWeb);
  const [devCalleeModalOpen, setDevCalleeModalOpen] = useState(false);
  const [testCallModalOpen, setTestCallModalOpen] = useState(false);
  const devMockMode = testModeEnabled;
  const [selectedLocale, setSelectedLocale] = useState<AppLocale>("zh");
  const [nativeLanguage, setNativeLanguage] = useState<CallerLanguage>("zh");
  const [user, setUser] = useState<UserProfile | null>(null);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [billingMode, setBillingMode] = useState("manual_prepaid");
  const [topUpPackages, setTopUpPackages] = useState<TopUpPackage[]>([]);
  const [isTopUpSheetOpen, setIsTopUpSheetOpen] = useState(false);
  const [destinationNumber, setDestinationNumber] = useState(defaultNumber);
  const [callerLanguage, setCallerLanguage] = useState<CallerLanguage>("zh");
  const [calleeLanguage] = useState<CalleeLanguage>("ja");
  const [activeCall, setActiveCall] = useState<CallSession | null>(null);
  const [agentScreen, setAgentScreen] = useState<"none" | "form" | "result">(
    "none",
  );
  const [agentDestinationNumber, setAgentDestinationNumber] = useState("");
  const [agentTaskGoal, setAgentTaskGoal] = useState("");
  const [agentCallbackNumber, setAgentCallbackNumber] = useState("");
  const [agentPermissionAccepted, setAgentPermissionAccepted] = useState(false);
  const [agentStatus, setAgentStatus] = useState<
    "idle" | "dialing" | "talking" | "wrapping_up" | "completed"
  >("idle");
  const [agentOutcome, setAgentOutcome] = useState<{
    outcome: "success" | "partial" | "failure" | "voicemail" | "no_answer";
    summary: string;
  } | null>(null);
  const [history, setHistory] = useState<CallSession[]>([]);
  const [expandedHistory, setExpandedHistory] = useState<Set<string>>(new Set());
  const [historyCollapsed, setHistoryCollapsed] = useState(true);
  const [recordingsTarget, setRecordingsTarget] = useState<{
    callSessionId: string;
    destinationNumber: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lowConfidenceMessage, setLowConfidenceMessage] = useState<
    string | null
  >(null);
  const [browserVoiceStatus, setBrowserVoiceStatus] = useState<string | null>(
    null,
  );
  const [isBusy, setIsBusy] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [speakerEnabled, setSpeakerEnabled] = useState(true);
  const [showTranscriptPanel, setShowTranscriptPanel] = useState(true);
  const [activePartials, setActivePartials] = useState<{
    caller: PartialTranscript | null;
    callee: PartialTranscript | null;
  }>({ caller: null, callee: null });
  const [lowConfidenceIds, setLowConfidenceIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [signupName, setSignupName] = useState("");
  const [callConsentConfirmed, setCallConsentConfirmed] = useState(false);
  const [acceptableUseConfirmed, setAcceptableUseConfirmed] = useState(false);
  const [activeLegalSheet, setActiveLegalSheet] = useState<LegalDocumentKind | null>(
    null,
  );
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const socketRef = useRef<{ close: () => void } | null>(null);
  const browserVoiceSessionRef = useRef<BrowserVoiceSession | null>(null);
  const preparedBrowserVoiceClientRef = useRef<PreparedBrowserVoiceClient | null>(
    null,
  );
  const activeCallIdRef = useRef<string | null>(null);
  const pulseAnim = useRef(new Animated.Value(0)).current;
  const ringbackPlayerRef = useRef<AudioPlayer | null>(null);

  const locale = user?.appLocale ?? selectedLocale;
  const copy = getCopy(locale);
  const languageCopy = languageLabels[locale];
  const activeLegalDocument = activeLegalSheet
    ? getLegalDocument(locale, activeLegalSheet)
    : null;

  const activeTranscript = useMemo(
    () => activeCall?.transcript ?? [],
    [activeCall],
  );
  const activeCallStageLabel = activeCall
    ? getCallStageLabel(copy, activeCall, browserVoiceStatus)
    : null;
  const activeCallHint = activeCall
    ? getCallHint(copy, activeCall, browserVoiceStatus)
    : null;
  const activeCallDurationLabel =
    activeCall && activeCall.startedAt
      ? getCallDurationLabel(activeCall, clockNow)
      : null;
  const callBadgeLabel = activeCall
    ? getCallBadgeLabel(activeCall.destinationNumber)
    : "";
  const isPickedUp = Boolean(activeCall?.startedAt);
  const isPrePickup =
    !!activeCall &&
    !isPickedUp &&
    activeCall.status !== "ended" &&
    activeCall.status !== "failed";

  useEffect(() => {
    browserVoiceSessionRef.current?.setMuted(isMuted);
  }, [isMuted]);

  useEffect(() => {
    browserVoiceSessionRef.current?.setSpeakerEnabled(speakerEnabled);
  }, [speakerEnabled]);

  const destinationHydratedRef = useRef(false);

  useEffect(() => {
    (async () => {
      const saved = await loadUserLocally();
      if (saved) {
        setUser(saved.user);
        setWallet(saved.wallet);
        setBillingMode("manual_prepaid");
        setCallerLanguage(saved.user.defaultCallerLanguage);
      }
      const savedNumber = await storageGet(LAST_DESTINATION_KEY);
      // Ignore a stored value equal to the placeholder default — prior builds
      // had a race that saved the default over the user's real number, so
      // treat that exact match as "nothing saved yet."
      if (savedNumber && savedNumber !== defaultNumber) {
        setDestinationNumber(savedNumber);
      }
      // Only allow the persistence effect to write after hydration completes,
      // otherwise the first render races the async load and clobbers the
      // saved number with `defaultNumber`.
      destinationHydratedRef.current = true;
    })();
  }, []);

  useEffect(() => {
    if (!destinationHydratedRef.current) return;
    if (!destinationNumber) return;
    storageSet(LAST_DESTINATION_KEY, destinationNumber).catch(() => {});
  }, [destinationNumber]);

  useEffect(() => {
    (async () => {
      const saved = await storageGet(TEST_MODE_STORAGE_KEY);
      // "1" = explicit on, "0" = explicit off, absent = use platform default
      // (web defaults on, native defaults off).
      if (saved === "1") setTestModeEnabled(true);
      else if (saved === "0") setTestModeEnabled(false);
    })();
  }, []);

  const toggleTestMode = async () => {
    const next = !testModeEnabled;
    setTestModeEnabled(next);
    try {
      await storageSet(TEST_MODE_STORAGE_KEY, next ? "1" : "0");
    } catch {}
    Alert.alert(
      next ? "Test mode ON" : "Test mode OFF",
      next
        ? "Calls will skip Twilio. Tap 'Start test call' in the banner below to open the combined caller/callee screen."
        : "Back to normal — calls will dial Twilio.",
    );
  };

  useEffect(() => {
    if (!user) {
      setCallerLanguage(nativeLanguage);
    }
  }, [nativeLanguage, user]);

  useEffect(() => {
    return () => {
      browserVoiceSessionRef.current?.destroy();
      browserVoiceSessionRef.current = null;
      preparedBrowserVoiceClientRef.current?.destroy();
      preparedBrowserVoiceClientRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!activeCall) {
      setClockNow(Date.now());
      return;
    }

    setClockNow(Date.now());
    const intervalId = setInterval(() => {
      setClockNow(Date.now());
    }, 1_000);

    return () => {
      clearInterval(intervalId);
    };
  }, [activeCall?.id, activeCall?.startedAt, activeCall?.status]);

  useEffect(() => {
    setShowTranscriptPanel(false);
  }, [activeCall?.id]);

  useEffect(() => {
    if (!isPrePickup) {
      pulseAnim.stopAnimation();
      pulseAnim.setValue(0);
      return;
    }

    // iPhone-like outgoing-call halo: soft expanding pulse while waiting for
    // the callee to pick up. Paired with a looped US-pattern ringback tone.
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1400,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 0,
          duration: 0,
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => {
      loop.stop();
    };
  }, [isPrePickup, pulseAnim]);

  useEffect(() => {
    const stopRingback = () => {
      const player = ringbackPlayerRef.current;
      if (!player) return;
      try {
        player.pause();
        player.remove();
      } catch {
        /* ignore */
      }
      ringbackPlayerRef.current = null;
    };

    if (!isPrePickup) {
      stopRingback();
      return;
    }

    // Don't call setAudioModeAsync — expo-two-way-audio's initialize() has
    // already set AVAudioSession to .playAndRecord / .voiceChat with
    // .defaultToSpeaker so the mic stays live and playback routes loudly. Any
    // setAudioModeAsync call here would overwrite those options and either
    // mute the mic or drop audio onto the earpiece.
    try {
      // keepAudioSessionActive:true prevents expo-audio's pause()/remove from
      // calling AVAudioSession.setActive(false), which would deactivate the
      // shared session that expo-two-way-audio uses for playPCMData — the
      // actual cause of "text shows but no audio" after the callee answers.
      const player = createAudioPlayer(RINGBACK_SOURCE, {
        keepAudioSessionActive: true,
      });
      player.loop = true;
      player.volume = 0.9;
      ringbackPlayerRef.current = player;
      Promise.resolve(player.play()).catch(() => {});
    } catch {
      /* audio subsystem unavailable */
    }

    return stopRingback;
  }, [isPrePickup]);

  const connectPreparedBrowserVoiceCall = async (callSessionId: string) => {
    if (browserVoiceSessionRef.current || !preparedBrowserVoiceClientRef.current) {
      return;
    }

    const preparedClient = preparedBrowserVoiceClientRef.current;
    const browserSession = await preparedClient.connect(callSessionId);
    browserVoiceSessionRef.current = browserSession;
    browserSession.setMuted(isMuted);
    browserSession.setSpeakerEnabled(speakerEnabled);
  };

  const stopVoiceRuntime = useCallback(() => {
    setLowConfidenceMessage(null);
    setBrowserVoiceStatus(null);
    browserVoiceSessionRef.current?.destroy();
    browserVoiceSessionRef.current = null;
    preparedBrowserVoiceClientRef.current?.destroy();
    preparedBrowserVoiceClientRef.current = null;
    setIsMuted(false);
  }, []);

  const finishCallSession = useCallback(
    async (session: CallSession) => {
      if (activeCallIdRef.current === session.id) {
        activeCallIdRef.current = null;
        setActiveCall(null);
      }
      stopVoiceRuntime();
      let transcript = session.transcript;
      try {
        transcript = await fetchTranscript(session.id);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { flow: "call-transcript-finalize" },
          extra: { callSessionId: session.id },
        });
      }
      setHistory((current) => {
        const updatedSession = {
          ...session,
          transcript,
        };
        const next = current.filter((entry) => entry.id !== updatedSession.id);
        return [updatedSession, ...next];
      });
    },
    [stopVoiceRuntime],
  );

  const applyCanonicalActiveCall = useCallback(
    async (session: CallSession) => {
      if (activeCallIdRef.current !== session.id) {
        return;
      }

      if (
        session.status === "ended" ||
        session.status === "failed" ||
        session.translationState === "ended" ||
        session.translationState === "failed"
      ) {
        await finishCallSession(session);
        return;
      }

      setActiveCall((current) => {
        if (!current || current.id !== session.id) {
          return session;
        }
        return {
          ...session,
          transcript:
            current.transcript.length > session.transcript.length
              ? current.transcript
              : session.transcript,
        };
      });
    },
    [finishCallSession],
  );

  const syncActiveCall = useCallback(
    async (callSessionId: string) => {
      try {
        const session = await fetchCallSession(callSessionId);
        await applyCanonicalActiveCall(session);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { flow: "active-call-sync" },
          extra: { callSessionId },
        });
      }
    },
    [applyCanonicalActiveCall],
  );

  const applyStartResponse = useCallback((session: CallSession) => {
    if (activeCallIdRef.current !== session.id) {
      return;
    }
    setActiveCall((current) => {
      if (
        current?.id === session.id &&
        !session.startedAt &&
        (current.startedAt ||
          current.status === "connected" ||
          current.status === "translating" ||
          current.status === "ended" ||
          current.status === "failed")
      ) {
        return current;
      }
      return session;
    });
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    let isMounted = true;
    fetchCallSessions(user.id)
      .then((sessions) => {
        if (isMounted) {
          setHistory(sessions);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError(copy.genericError);
        }
      });

    const socket = connectRealtime(user, {
      onOpen: () => {
        const callSessionId = activeCallIdRef.current;
        if (callSessionId) {
          void syncActiveCall(callSessionId);
        }
      },
      onEvent: async (event) => {
        if (event.type === "wallet.updated") {
          setWallet(event.wallet);
          if (user) {
            void saveUserLocally(user, event.wallet);
          }
          return;
        }

        if ("callSession" in event) {
          console.log(
            "[sse.recv]",
            JSON.stringify({
              type: event.type,
              sid: event.callSession.id.slice(0, 8),
              activeSid: activeCallIdRef.current?.slice(0, 8) ?? null,
              match: activeCallIdRef.current === event.callSession.id,
              status: event.callSession.status,
              providerCallStatus: event.callSession.providerCallStatus,
              callerLegState: event.callSession.callerLegState,
              pstnLegState: event.callSession.pstnLegState,
              hasStartedAt: Boolean(event.callSession.startedAt),
            }),
          );
          await applyCanonicalActiveCall(event.callSession);

          if (
            (event.type === "call.connected" ||
              event.callSession.providerCallStatus === "in-progress") &&
            preparedBrowserVoiceClientRef.current &&
            !browserVoiceSessionRef.current
          ) {
            try {
              await connectPreparedBrowserVoiceCall(event.callSession.id);
            } catch (connectError) {
              Sentry.captureException(connectError, {
                tags: { flow: "voice-connect" },
                extra: { callSessionId: event.callSession.id },
              });
              setBrowserVoiceStatus("error");
              setError(
                connectError instanceof Error
                  ? connectError.message
                  : copy.genericError,
              );
            }
          }
        }

        if (event.type === "call.translation.segment") {
          setActiveCall((current) => {
            if (!current || current.id !== event.callSessionId) {
              return current;
            }

            return {
              ...current,
              transcript: [...current.transcript, event.segment],
            };
          });
          setActivePartials((current) => {
            const existing = current[event.segment.speaker];
            if (
              existing &&
              existing.startedAtMs <= event.segment.startedAtMs
            ) {
              return { ...current, [event.segment.speaker]: null };
            }
            return current;
          });
        }

        if (event.type === "call.translation.segment.partial") {
          setActivePartials((current) => ({
            ...current,
            [event.speaker]: {
              speaker: event.speaker,
              sourceLanguage: event.sourceLanguage,
              sourceText: event.sourceText,
              startedAtMs: event.startedAtMs,
            },
          }));
        }

        if (event.type === "call.translation.low_confidence") {
          setLowConfidenceMessage(event.message);
          setLowConfidenceIds((current) => {
            const next = new Set(current);
            next.add(event.segment.id);
            return next;
          });
        }

        if (event.type === "call.agent.status") {
          setAgentStatus(event.status);
          return;
        }

        if (event.type === "call.agent.outcome") {
          setAgentOutcome({
            outcome: event.outcome,
            summary: event.summary,
          });
          setAgentStatus("completed");
          setActiveCall(null);
          setAgentScreen("result");
          return;
        }
      },
      onError: () => {
        setError(copy.genericError);
      },
    });

    socketRef.current = socket;

    return () => {
      isMounted = false;
      socket.close();
      socketRef.current = null;
    };
  }, [applyCanonicalActiveCall, copy.genericError, syncActiveCall, user]);

  useEffect(() => {
    if (!user) {
      setTopUpPackages([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await configurePurchases(user.id);
        const packages = await fetchTopUpPackages();
        if (!cancelled) {
          setTopUpPackages(packages);
          if (packages.length === 0) {
            console.warn("RevenueCat returned 0 packages — check offerings in the RC dashboard");
          }
        }
      } catch (err) {
        console.error("Purchase setup failed:", err instanceof Error ? err.message : String(err));
        Sentry.captureException(err, { tags: { flow: "purchase-setup" } });
        if (!cancelled) {
          setTopUpPackages([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleSignup = async () => {
    if (!acceptedLegal) {
      setError(copy.legalConsentLabel);
      return;
    }

    setIsBusy(true);
    setError(null);

    try {
      const trimmedName = signupName.trim();
      const response = await signUp({
        name: trimmedName || "Caller",
        appLocale: selectedLocale,
        defaultCallerLanguage: nativeLanguage,
        acceptedLegal: true,
      });
      setUser(response.user);
      setWallet(response.wallet);
      setBillingMode(response.billingMode);
      setCallerLanguage(response.user.defaultCallerLanguage);
      setCallConsentConfirmed(false);
      setAcceptableUseConfirmed(false);
      setHistory([]);
      await saveUserLocally(response.user, response.wallet);
    } catch (error) {
      Sentry.captureException(error, { tags: { flow: "signup" } });
      setError(copy.signUpError);
    } finally {
      setIsBusy(false);
    }
  };

  const handleTopUp = async () => {
    if (!user) {
      return;
    }
    if (!isNativePlatform) {
      if (showDemoTopUp) {
        return handleDemoTopUp();
      }
      setError(copy.genericError);
      return;
    }
    setError(null);
    if (topUpPackages.length === 0) {
      try {
        const packages = await fetchTopUpPackages();
        setTopUpPackages(packages);
        if (packages.length === 0) {
          Sentry.captureMessage("No top-up packages returned from RevenueCat", {
            level: "warning",
            tags: { flow: "purchase-topup" },
          });
          setError(`${copy.genericError} (no packages available)`);
          return;
        }
      } catch (err) {
        Sentry.captureException(err, { tags: { flow: "purchase-fetch" } });
        setError(`${copy.genericError} (${err instanceof Error ? err.message : String(err)})`);
        return;
      }
    }
    setIsTopUpSheetOpen(true);
  };

  const handlePurchaseTopUp = async (pkg: TopUpPackage) => {
    if (!user) {
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      const outcome = await purchaseTopUpPackage(pkg.packageIdentifier);
      if (outcome.kind === "cancelled") {
        return;
      }
      const response = await fulfillPurchase({
        userId: user.id,
        productId: outcome.productId,
        transactionId: outcome.transactionId,
      });
      setWallet(response.wallet);
      await saveUserLocally(user, response.wallet);
      setIsTopUpSheetOpen(false);
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      console.error("Purchase failed:", detail);
      Sentry.captureException(error, {
        tags: { flow: "purchase-topup" },
        extra: { detail },
      });
      setError(`${copy.genericError} (${detail})`);
    } finally {
      setIsBusy(false);
    }
  };

  const handleRestorePurchases = async () => {
    if (!user || !isNativePlatform) {
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      await restorePurchases();
      // Restore is idempotent on the backend because /purchases/fulfill
      // dedupes by transactionId; no action needed here beyond refreshing
      // local wallet state which the worker will push via the realtime
      // socket after any replay.
    } catch {
      setError(copy.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  const handleDemoTopUp = async () => {
    if (!user) {
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      const updated = await topUpWallet(user.id, demoTopUpAmount);
      setWallet(updated);
      await saveUserLocally(user, updated);
    } catch {
      setError(copy.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  const handleStartCall = async () => {
    if (!user || !wallet) {
      return;
    }
    if (!callConsentConfirmed) {
      setError(copy.callConsentLabel);
      return;
    }
    if (!acceptableUseConfirmed) {
      setError(copy.acceptableUseLabel);
      return;
    }
    if (isBlockedJapanServiceNumber(destinationNumber)) {
      setError(copy.shortCodeBlockedNotice);
      return;
    }

    setIsBusy(true);
    setError(null);
    setLowConfidenceMessage(null);
    setLowConfidenceIds(new Set());
    setActivePartials({ caller: null, callee: null });
    setBrowserVoiceStatus("starting");

    const request: CreateCallSessionRequest = {
      userId: user.id,
      destinationNumberE164: destinationNumber,
      callerLanguage,
      calleeLanguage,
      mode: "translator",
      record: RECORDING_ENABLED,
    };
    let createdSession: CallSession | null = null;
    const isReviewerUser = user.id.startsWith("reviewer-");

    try {
      createdSession = await createCallSession(request);
      activeCallIdRef.current = createdSession.id;
      setActiveCall(createdSession);
      if (isReviewerUser) {
        setBrowserVoiceStatus("browser-device-ready");
        const started = await startCallSession(user.id, createdSession.id, {
          record: RECORDING_ENABLED,
          mock: devMockMode,
        });
        applyStartResponse(started);
        await applyCanonicalActiveCall(started);
        return;
      }
      browserVoiceSessionRef.current?.destroy();
      preparedBrowserVoiceClientRef.current?.destroy();
      preparedBrowserVoiceClientRef.current = await prepareBrowserVoiceClient({
        userId: user.id,
        identity: `nativecall-${user.id}`,
        handlers: {
          onDisconnected: () => {
            browserVoiceSessionRef.current = null;
            setIsMuted(false);
            setBrowserVoiceStatus("disconnected");
          },
          onError: (message) => {
            Sentry.captureMessage(message, {
              level: "error",
              tags: { flow: "voice-runtime" },
            });
            setBrowserVoiceStatus("error");
            setError(message);
          },
          onWarning: (message) => {
            setLowConfidenceMessage(message);
          },
          onMuteChanged: (muted) => {
            setIsMuted(muted);
          },
          onStatusChanged: (status) => {
            setBrowserVoiceStatus(status);
          },
        },
      });
      setBrowserVoiceStatus("browser-device-ready");
      const started = await startCallSession(user.id, createdSession.id, {
        record: RECORDING_ENABLED,
        mock: devMockMode,
      });
      applyStartResponse(started);
      void syncActiveCall(started.id);
      try {
        await connectPreparedBrowserVoiceCall(started.id);
      } catch (connectError) {
        Sentry.captureException(connectError, {
          tags: { flow: "voice-connect-immediate" },
          extra: { callSessionId: started.id },
        });
      }
    } catch (startError) {
      if (createdSession) {
        try {
          await cancelCallSession(
            user.id,
            createdSession.id,
            startError instanceof Error ? startError.message : copy.genericError,
          );
        } catch {}
      }
      browserVoiceSessionRef.current?.destroy();
      browserVoiceSessionRef.current = null;
      preparedBrowserVoiceClientRef.current?.destroy();
      preparedBrowserVoiceClientRef.current = null;
      setBrowserVoiceStatus(null);
      activeCallIdRef.current = null;
      setActiveCall(null);
      setError(
        startError instanceof Error ? startError.message : copy.genericError,
      );
    } finally {
      setIsBusy(false);
    }
  };

  const handleEndCall = async () => {
    if (!user || !activeCall) {
      return;
    }

    setIsBusy(true);

    try {
      browserVoiceSessionRef.current?.disconnect();
      browserVoiceSessionRef.current?.destroy();
      browserVoiceSessionRef.current = null;
      preparedBrowserVoiceClientRef.current?.destroy();
      preparedBrowserVoiceClientRef.current = null;
      setBrowserVoiceStatus(null);
      const ended = await endCallSession(user.id, activeCall.id);
      const transcript = await fetchTranscript(ended.id);
      setHistory((current) => [
        {
          ...ended,
          transcript,
        },
        ...current.filter((entry) => entry.id !== ended.id),
      ]);
      activeCallIdRef.current = null;
      setActiveCall(null);
      setLowConfidenceMessage(null);
      setIsMuted(false);
    } catch {
      setError(copy.genericError);
    } finally {
      setIsBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) {
      return;
    }

    setIsBusy(true);
    setError(null);

    try {
      await deleteAccount(user.id);
      await clearUserLocally();
      browserVoiceSessionRef.current?.destroy();
      browserVoiceSessionRef.current = null;
      preparedBrowserVoiceClientRef.current?.destroy();
      preparedBrowserVoiceClientRef.current = null;
      socketRef.current?.close();
      socketRef.current = null;
      await signOutPurchases();
      setShowDeleteAccountConfirm(false);
      setActiveCall(null);
      setHistory([]);
      setWallet(null);
      setUser(null);
      setTopUpPackages([]);
      setIsTopUpSheetOpen(false);
      setCallConsentConfirmed(false);
      setAcceptableUseConfirmed(false);
      setIsMuted(false);
      setBrowserVoiceStatus(null);
      setLowConfidenceMessage(null);
    } catch (deleteError) {
      setError(
        deleteError instanceof Error ? deleteError.message : copy.genericError,
      );
    } finally {
      setIsBusy(false);
    }
  };

  const renderLanguagePicker = <T extends SupportedLanguage>(
    value: T,
    onChange: (next: T) => void,
    options: readonly T[],
  ) => (
    <View style={styles.segmentRow}>
      {options.map((language) => {
        const active = language === value;
        return (
          <Pressable
            key={language}
            onPress={() => onChange(language)}
            style={[styles.segmentButton, active && styles.segmentButtonActive]}
          >
            <Text
              style={[
                styles.segmentButtonText,
                active && styles.segmentButtonTextActive,
              ]}
            >
              {languageCopy[language]}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );

  const renderReadOnlyLanguage = (language: SupportedLanguage) => (
    <View style={styles.segmentRow}>
      <View style={[styles.segmentButton, styles.segmentButtonActive]}>
        <Text style={[styles.segmentButtonText, styles.segmentButtonTextActive]}>
          {languageCopy[language]}
        </Text>
      </View>
    </View>
  );

  const renderTranscript = (transcript: TranscriptSegment[]) => (
    <View style={styles.transcriptList}>
      {transcript.map((segment) => (
        <View key={segment.id} style={styles.transcriptCard}>
          <Text style={styles.transcriptSpeaker}>
            {segment.speaker === "caller"
              ? `${copy.transcript} • ${copy.activeCall}`
              : `${copy.transcript} • Remote`}
          </Text>
          <Text style={styles.transcriptSource}>{segment.sourceText}</Text>
          <Text style={styles.transcriptTranslated}>{segment.translatedText}</Text>
        </View>
      ))}
    </View>
  );

  const renderChecklistRow = (
    checked: boolean,
    onPress: () => void,
    label: string,
  ) => (
    <Pressable onPress={onPress} style={styles.checklistRow}>
      <View style={[styles.checklistBox, checked && styles.checklistBoxChecked]}>
        {checked ? <View style={styles.checklistBoxInner} /> : null}
      </View>
      <Text style={styles.checklistLabel}>{label}</Text>
    </Pressable>
  );

  const renderLegalAction = (
    label: string,
    kind: LegalDocumentKind,
    url: string,
  ) => (
    <Pressable
      onPress={() => setActiveLegalSheet(kind)}
      onLongPress={() => {
        void openExternalUrl(url);
      }}
      style={styles.legalActionButton}
    >
      <Text style={styles.legalActionText}>{label}</Text>
      <Text style={styles.legalActionMeta}>{copy.openLabel}</Text>
    </Pressable>
  );

  const renderHowItWorksStep = (
    indexLabel: string,
    title: string,
    body: string,
  ) => (
    <View style={styles.onboardingStepCard}>
      <View style={styles.onboardingStepBadge}>
        <Text style={styles.onboardingStepBadgeText}>{indexLabel}</Text>
      </View>
      <View style={styles.onboardingStepCopy}>
        <Text style={styles.onboardingStepTitle}>{title}</Text>
        <Text style={styles.onboardingStepBody}>{body}</Text>
      </View>
    </View>
  );

  const legalSheetModal = (
    <Modal
      visible={activeLegalSheet !== null}
      animationType="slide"
      transparent
      onRequestClose={() => setActiveLegalSheet(null)}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.modalSheet}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>
              {activeLegalDocument?.title ?? copy.privacyPolicyLabel}
            </Text>
            <Pressable onPress={() => setActiveLegalSheet(null)} style={styles.modalHeaderButton}>
              <Text style={styles.modalHeaderButtonText}>{copy.closeLabel}</Text>
            </Pressable>
          </View>
          <ScrollView
            style={styles.modalScroll}
            contentContainerStyle={styles.modalContent}
            showsVerticalScrollIndicator={false}
          >
            {activeLegalDocument ? (
              <>
                <Text style={styles.modalIntro}>{activeLegalDocument.intro}</Text>
                {activeLegalDocument.sections.map((section) => (
                  <View key={section.heading} style={styles.modalSection}>
                    <Text style={styles.modalSectionHeading}>{section.heading}</Text>
                    <Text style={styles.modalSectionBody}>{section.body}</Text>
                  </View>
                ))}
              </>
            ) : null}
          </ScrollView>
          <View style={styles.modalFooter}>
            <Pressable
              onPress={() => {
                if (activeLegalSheet === "privacy") {
                  void openExternalUrl(legalLinks.privacyUrl);
                } else if (activeLegalSheet === "terms") {
                  void openExternalUrl(legalLinks.termsUrl);
                } else if (activeLegalSheet === "support") {
                  void openExternalUrl(legalLinks.supportUrl);
                }
              }}
              style={styles.modalSecondaryButton}
            >
              <Text style={styles.modalSecondaryButtonText}>
                {copy.openWebPageLabel}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );

  const deleteAccountModal = (
    <Modal
      visible={showDeleteAccountConfirm}
      animationType="fade"
      transparent
      onRequestClose={() => setShowDeleteAccountConfirm(false)}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.confirmSheet}>
          <Text style={styles.confirmTitle}>{copy.deleteAccountLabel}</Text>
          <Text style={styles.confirmBody}>{copy.deleteAccountBody}</Text>
          <View style={styles.confirmActions}>
            <Pressable
              onPress={() => setShowDeleteAccountConfirm(false)}
              style={styles.modalSecondaryButton}
            >
              <Text style={styles.modalSecondaryButtonText}>{copy.cancelLabel}</Text>
            </Pressable>
            <Pressable onPress={handleDeleteAccount} style={styles.dangerButton}>
              <Text style={styles.dangerButtonText}>{copy.deleteAccountConfirmLabel}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );

  const topUpSheetModal = (
    <Modal
      visible={isTopUpSheetOpen}
      animationType="slide"
      transparent
      onRequestClose={() => setIsTopUpSheetOpen(false)}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.modalSheet}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{copy.topUpCta}</Text>
            <Pressable
              onPress={() => setIsTopUpSheetOpen(false)}
              style={styles.modalHeaderButton}
            >
              <Text style={styles.modalHeaderButtonText}>{copy.closeLabel}</Text>
            </Pressable>
          </View>
          <ScrollView
            style={styles.modalScroll}
            contentContainerStyle={styles.modalContent}
            showsVerticalScrollIndicator={false}
          >
            {topUpPackages.length === 0 ? (
              <Text style={styles.modalIntro}>{copy.genericError}</Text>
            ) : (
              topUpPackages.map((pkg) => (
                <Pressable
                  key={pkg.packageIdentifier}
                  onPress={() => handlePurchaseTopUp(pkg)}
                  style={styles.modalSection}
                  disabled={isBusy}
                >
                  <Text style={styles.modalSectionHeading}>
                    {creditsToMinutes(pkg.credits, wallet?.ratePerMinuteCredits)} {copy.minutesUnit} · {pkg.priceString}
                  </Text>
                  <Text style={styles.modalSectionBody}>{pkg.description}</Text>
                </Pressable>
              ))
            )}
          </ScrollView>
          <View style={styles.modalFooter}>
            <Pressable
              onPress={handleRestorePurchases}
              style={styles.modalSecondaryButton}
              disabled={isBusy}
            >
              <Text style={styles.modalSecondaryButtonText}>
                {copy.restorePurchasesCta}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );

  if (activeCall) {
    if (activeCall.mode === "autonomous") {
      return (
        <SafeAreaView style={styles.callSafeArea}>
          <StatusBar style="light" />
          <View style={{ flex: 1, padding: 20 }}>
            <View
              style={{
                padding: 10,
                backgroundColor: "rgba(59,130,246,0.15)",
                borderWidth: 1,
                borderColor: "rgba(59,130,246,0.4)",
                borderRadius: 8,
                marginBottom: 12,
              }}
            >
              <Text style={{ color: "#93c5fd", fontWeight: "600" }}>
                AI agent speaking on behalf of {user?.name ?? "you"}
              </Text>
              <Text style={{ color: "#cbd5e1", fontSize: 12, marginTop: 2 }}>
                Powered by OpenAI. Audio is processed by OpenAI and Twilio.
              </Text>
            </View>
            <Text
              style={{
                fontSize: 18,
                fontWeight: "600",
                marginBottom: 16,
                color: "#fff",
              }}
            >
              {agentStatus === "dialing" && "Dialing…"}
              {agentStatus === "talking" && "Agent talking…"}
              {agentStatus === "wrapping_up" && "Wrapping up…"}
              {agentStatus === "completed" && "Completed"}
              {agentStatus === "idle" && "Starting…"}
            </Text>
            <Text style={{ color: "#888", marginBottom: 16 }}>
              {activeCall.destinationNumber}
            </Text>

            <ScrollView style={{ flex: 1 }}>
              {activeTranscript.map((segment) => (
                <View
                  key={segment.id}
                  style={{
                    marginBottom: 12,
                    padding: 8,
                    backgroundColor: "rgba(255,255,255,0.05)",
                    borderRadius: 8,
                  }}
                >
                  <Text style={{ fontWeight: "600", color: "#fff" }}>
                    {segment.speaker === "caller" ? "Agent" : "Callee"}
                  </Text>
                  <Text style={{ color: "#fff" }}>{segment.sourceText}</Text>
                  {segment.translatedText ? (
                    <Text style={{ color: "#aaa", marginTop: 4 }}>
                      {segment.translatedText}
                    </Text>
                  ) : null}
                </View>
              ))}
            </ScrollView>

            <Pressable
              onPress={async () => {
                if (!user) return;
                try {
                  setIsBusy(true);
                  await cancelCallSession(
                    user.id,
                    activeCall.id,
                    "Cancelled by user.",
                  );
                } catch (caughtError) {
                  setError(String(caughtError));
                } finally {
                  setIsBusy(false);
                }
              }}
              style={{
                backgroundColor: "#dc2626",
                padding: 16,
                borderRadius: 12,
                marginTop: 16,
                alignItems: "center",
              }}
            >
              <Text style={{ color: "#fff", fontWeight: "600" }}>End call</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      );
    }

    return (
      <>
        {legalSheetModal}
        {deleteAccountModal}
        {topUpSheetModal}
        <SafeAreaView style={styles.callSafeArea}>
          <StatusBar style="light" />
          <View style={styles.callBackdropTop} />
          <View style={styles.callBackdropBottom} />
          <View style={styles.callShell}>
          <View style={styles.callTopBar}>
            <View style={styles.callStatusPill}>
              <Text style={styles.callStatusPillText}>{activeCallStageLabel}</Text>
            </View>
            {activeCallDurationLabel ? (
              <Text style={styles.callDurationText}>{activeCallDurationLabel}</Text>
            ) : null}
          </View>

          <View style={styles.callBody}>
            <View style={styles.callHeroPanel}>
              {isPrePickup ? (
                <>
                  <Animated.View
                    pointerEvents="none"
                    style={[
                      styles.callAvatarPulse,
                      {
                        opacity: pulseAnim.interpolate({
                          inputRange: [0, 1],
                          outputRange: [0.55, 0],
                        }),
                        transform: [
                          {
                            scale: pulseAnim.interpolate({
                              inputRange: [0, 1],
                              outputRange: [1, 1.45],
                            }),
                          },
                        ],
                      },
                    ]}
                  />
                  <Animated.View
                    pointerEvents="none"
                    style={[
                      styles.callAvatarPulse,
                      {
                        opacity: pulseAnim.interpolate({
                          inputRange: [0, 1],
                          outputRange: [0.35, 0],
                        }),
                        transform: [
                          {
                            scale: pulseAnim.interpolate({
                              inputRange: [0, 1],
                              outputRange: [1, 1.75],
                            }),
                          },
                        ],
                      },
                    ]}
                  />
                </>
              ) : (
                <View style={styles.callAvatarHalo} />
              )}
              <View style={styles.callAvatar}>
                <Text style={styles.callAvatarText}>{callBadgeLabel}</Text>
              </View>
              <Text style={styles.callRemoteNumber}>{activeCall.destinationNumber}</Text>
              {devMockMode ? (
                <Text selectable style={styles.devMockSessionId}>
                  mock session id: {activeCall.id}
                </Text>
              ) : null}
              {isPrePickup ? (
                <Text style={styles.callPrePickupStatus}>
                  {activeCallStageLabel}
                </Text>
              ) : (
                <Text style={styles.callSecondaryText}>
                  {languageCopy[activeCall.callerLanguage]} →{" "}
                  {languageCopy[activeCall.calleeLanguage]}
                </Text>
              )}
              {isPrePickup ? null : (
                <Text style={styles.callHintText}>{activeCallHint}</Text>
              )}
              {lowConfidenceMessage ? (
                <View style={styles.callWarningBanner}>
                  <Text style={styles.callWarningText}>
                    {copy.lowConfidence}: {lowConfidenceMessage}
                  </Text>
                </View>
              ) : null}
              {error ? <Text style={styles.callInlineError}>{error}</Text> : null}
            </View>

            {isPrePickup ? null : (
              <View style={styles.phoneControlGrid}>
                <Pressable
                  onPress={() => setIsMuted((current) => !current)}
                  style={[
                    styles.phoneCircleButton,
                    isMuted && styles.phoneCircleButtonLight,
                  ]}
                >
                  <Text
                    style={[
                      styles.phoneCircleButtonLabel,
                      isMuted && styles.phoneCircleButtonLabelDark,
                    ]}
                  >
                    {copy.mute}
                  </Text>
                  <Text
                    style={[
                      styles.phoneCircleButtonState,
                      isMuted && styles.phoneCircleButtonStateDark,
                    ]}
                  >
                    {isMuted ? "ON" : "OFF"}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => setShowTranscriptPanel((current) => !current)}
                  style={[
                    styles.phoneCircleButton,
                    showTranscriptPanel && styles.phoneCircleButtonLight,
                  ]}
                >
                  <Text
                    style={[
                      styles.phoneCircleButtonLabel,
                      showTranscriptPanel && styles.phoneCircleButtonLabelDark,
                    ]}
                  >
                    {copy.transcript}
                  </Text>
                  <Text
                    style={[
                      styles.phoneCircleButtonState,
                      showTranscriptPanel && styles.phoneCircleButtonStateDark,
                    ]}
                  >
                    {showTranscriptPanel ? "OPEN" : "SHOW"}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => setSpeakerEnabled((current) => !current)}
                  style={[
                    styles.phoneCircleButton,
                    speakerEnabled && styles.phoneCircleButtonAccent,
                  ]}
                >
                  <Text
                    style={[
                      styles.phoneCircleButtonLabel,
                      speakerEnabled && styles.phoneCircleButtonLabelDark,
                    ]}
                  >
                    {copy.speaker}
                  </Text>
                  <Text
                    style={[
                      styles.phoneCircleButtonState,
                      speakerEnabled && styles.phoneCircleButtonStateDark,
                    ]}
                  >
                    {speakerEnabled ? "ON" : "OFF"}
                  </Text>
                </Pressable>
              </View>
            )}

            {isPrePickup ? null : showTranscriptPanel ? (
              <CallTranscriptChat
                segments={activeTranscript}
                partials={activePartials}
                youLabel={copy.transcriptYouLabel}
                themLabel={copy.transcriptThemLabel}
                waitingLabel={copy.transcriptWaiting}
                lowConfidenceIds={lowConfidenceIds}
                title={copy.transcript}
              />
            ) : (
              <View style={styles.callTranscriptCollapsed}>
                <Text style={styles.callTranscriptCollapsedTitle}>
                  {copy.transcript}
                </Text>
                <Text style={styles.callTranscriptCollapsedBody}>
                  {activeTranscript.length > 0
                    ? activeTranscript[activeTranscript.length - 1]
                        ?.translatedText ?? copy.transcriptWaiting
                    : copy.transcriptWaiting}
                </Text>
              </View>
            )}
          </View>

          <Pressable onPress={handleEndCall} style={styles.phoneEndButton}>
            <Text style={styles.phoneEndButtonText}>{copy.endCall}</Text>
          </Pressable>

          {isBusy ? (
            <ActivityIndicator color="#f7f3ed" size="small" style={styles.callSpinner} />
          ) : null}
          </View>
        </SafeAreaView>
      </>
    );
  }

  if (agentScreen === "form") {
    return (
      <SafeAreaView style={styles.callSafeArea}>
        <StatusBar style="light" />
        <ScrollView contentContainerStyle={{ padding: 20 }}>
          <Text
            style={{
              fontSize: 24,
              fontWeight: "600",
              marginBottom: 16,
              color: "#fff",
            }}
          >
            Send AI agent to call
          </Text>

          <Text style={{ color: "#fff", marginTop: 8 }}>
            Phone number (Japan, +81)
          </Text>
          <TextInput
            value={agentDestinationNumber}
            onChangeText={setAgentDestinationNumber}
            placeholder="+81..."
            placeholderTextColor="#888"
            keyboardType="phone-pad"
            style={{
              borderWidth: 1,
              borderColor: "#444",
              padding: 8,
              marginVertical: 8,
              color: "#fff",
              borderRadius: 8,
            }}
          />

          <Text style={{ color: "#fff", marginTop: 8 }}>
            What should the agent do?
          </Text>
          <TextInput
            value={agentTaskGoal}
            onChangeText={setAgentTaskGoal}
            placeholder="E.g. Book a table for 2 at Sushi Saito tomorrow at 7pm under the name Alex."
            placeholderTextColor="#888"
            multiline
            maxLength={500}
            style={{
              borderWidth: 1,
              borderColor: "#444",
              padding: 8,
              marginVertical: 8,
              minHeight: 120,
              color: "#fff",
              borderRadius: 8,
            }}
          />
          <Text style={{ color: "#888", textAlign: "right" }}>
            {agentTaskGoal.length}/500
          </Text>

          <Text style={{ color: "#fff", marginTop: 8 }}>
            Callback number (optional, used if voicemail)
          </Text>
          <TextInput
            value={agentCallbackNumber}
            onChangeText={setAgentCallbackNumber}
            placeholder="+1..."
            placeholderTextColor="#888"
            keyboardType="phone-pad"
            style={{
              borderWidth: 1,
              borderColor: "#444",
              padding: 8,
              marginVertical: 8,
              color: "#fff",
              borderRadius: 8,
            }}
          />

          <View
            style={{
              padding: 12,
              backgroundColor: "rgba(255,251,230,0.15)",
              marginVertical: 12,
              borderRadius: 8,
            }}
          >
            <Text style={{ color: "#fffbe6" }}>
              An AI assistant will dial this number and speak Japanese on your
              behalf. The agent will identify itself as AI at the start of the
              call. You will not be on the line.
            </Text>
          </View>

          <Pressable
            onPress={() => setAgentPermissionAccepted((v) => !v)}
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              padding: 12,
              backgroundColor: "rgba(255,255,255,0.05)",
              borderRadius: 8,
              marginVertical: 8,
            }}
          >
            <View
              style={{
                width: 22,
                height: 22,
                borderRadius: 4,
                borderWidth: 2,
                borderColor: agentPermissionAccepted ? "#3b82f6" : "#888",
                backgroundColor: agentPermissionAccepted
                  ? "#3b82f6"
                  : "transparent",
                marginRight: 10,
                marginTop: 2,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {agentPermissionAccepted ? (
                <Text
                  style={{
                    color: "#fff",
                    fontWeight: "700",
                    fontSize: 14,
                    lineHeight: 16,
                  }}
                >
                  ✓
                </Text>
              ) : null}
            </View>
            <Text style={{ color: "#fff", flex: 1 }}>
              I have permission to call this person, and this is not a prank,
              unsolicited, marketing, or automated-dialing call.
            </Text>
          </Pressable>

          <Pressable
            disabled={!agentPermissionAccepted}
            onPress={async () => {
              if (!user) {
                Alert.alert("Not signed in", "Please sign in first.");
                return;
              }
              if (!agentPermissionAccepted) {
                Alert.alert(
                  "Confirmation required",
                  "Please confirm you have permission to call this person.",
                );
                return;
              }
              if (!/^\+81\d{7,14}$/.test(agentDestinationNumber)) {
                Alert.alert(
                  "Invalid number",
                  "Please enter a Japanese phone number starting with +81.",
                );
                return;
              }
              if (agentTaskGoal.trim().length < 10) {
                Alert.alert(
                  "Task too short",
                  "Please describe what the agent should do (at least 10 characters).",
                );
                return;
              }
              if (
                agentCallbackNumber &&
                !/^\+[1-9]\d{7,14}$/.test(agentCallbackNumber)
              ) {
                Alert.alert(
                  "Invalid callback number",
                  "Please enter a valid international phone number, or leave blank.",
                );
                return;
              }

              try {
                setIsBusy(true);
                const created = await createCallSession({
                  userId: user.id,
                  destinationNumberE164: agentDestinationNumber,
                  callerLanguage: "en",
                  calleeLanguage: "ja",
                  mode: "autonomous",
                  taskGoal: agentTaskGoal.trim(),
                  callbackNumberE164: agentCallbackNumber || undefined,
                  record: RECORDING_ENABLED,
                });
                activeCallIdRef.current = created.id;
                setActiveCall(created);
                setAgentStatus("dialing");
                setAgentScreen("none");
                const started = await startCallSession(user.id, created.id, {
                  record: RECORDING_ENABLED,
                });
                applyStartResponse(started);
                void syncActiveCall(started.id);
              } catch (caughtError) {
                setError(String(caughtError));
              } finally {
                setIsBusy(false);
              }
            }}
            style={{
              backgroundColor: agentPermissionAccepted ? "#3b82f6" : "#2a2a2a",
              padding: 16,
              borderRadius: 12,
              marginTop: 16,
              alignItems: "center",
              opacity: agentPermissionAccepted ? 1 : 0.5,
            }}
          >
            <Text style={{ color: "#fff", fontWeight: "600" }}>
              Start Agent Call
            </Text>
          </Pressable>

          <Pressable
            onPress={() => setAgentScreen("none")}
            style={{ padding: 16, alignItems: "center" }}
          >
            <Text style={{ color: "#888" }}>Cancel</Text>
          </Pressable>
        </ScrollView>
      </SafeAreaView>
    );
  }

  if (agentScreen === "result") {
    return (
      <SafeAreaView style={styles.callSafeArea}>
        <StatusBar style="light" />
        <View
          style={{
            flex: 1,
            padding: 20,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Text style={{ fontSize: 64, marginBottom: 16 }}>
            {agentOutcome?.outcome === "success" && "✅"}
            {agentOutcome?.outcome === "partial" && "⚠️"}
            {agentOutcome?.outcome === "failure" && "❌"}
            {agentOutcome?.outcome === "voicemail" && "📩"}
            {agentOutcome?.outcome === "no_answer" && "📵"}
          </Text>
          <Text
            style={{
              fontSize: 22,
              fontWeight: "600",
              marginBottom: 12,
              color: "#fff",
            }}
          >
            {agentOutcome?.outcome === "success" && "Task complete"}
            {agentOutcome?.outcome === "partial" && "Partially complete"}
            {agentOutcome?.outcome === "failure" && "Couldn't complete"}
            {agentOutcome?.outcome === "voicemail" && "Left voicemail"}
            {agentOutcome?.outcome === "no_answer" && "No answer"}
          </Text>
          <Text
            style={{
              textAlign: "center",
              marginBottom: 24,
              color: "#ccc",
            }}
          >
            {agentOutcome?.summary}
          </Text>

          <Pressable
            onPress={() => {
              setAgentScreen("none");
              setAgentOutcome(null);
            }}
            style={{
              backgroundColor: "#3b82f6",
              padding: 16,
              borderRadius: 12,
              marginVertical: 8,
              paddingHorizontal: 32,
            }}
          >
            <Text style={{ color: "#fff", fontWeight: "600" }}>Done</Text>
          </Pressable>

          <Pressable
            onPress={() => {
              setAgentScreen("form");
              setAgentOutcome(null);
            }}
            style={{ padding: 16 }}
          >
            <Text style={{ color: "#888" }}>Try again</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  const testCallModal = (
    <Modal
      visible={testCallModalOpen}
      animationType="slide"
      onRequestClose={() => setTestCallModalOpen(false)}
    >
      <View style={{ flex: 1, backgroundColor: "#111" }}>
        {user && testCallModalOpen ? (
          <TestCallScreen
            userId={user.id}
            callerLanguage={callerLanguage}
            calleeLanguage={calleeLanguage}
            onClose={() => setTestCallModalOpen(false)}
          />
        ) : null}
      </View>
    </Modal>
  );

  const devCalleeModal = (
    <Modal
      visible={devCalleeModalOpen}
      animationType="slide"
      onRequestClose={() => setDevCalleeModalOpen(false)}
    >
      <View style={{ flex: 1, backgroundColor: "#111" }}>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "flex-end",
            padding: 12,
            paddingTop: 48,
            backgroundColor: "#111",
          }}
        >
          <Pressable
            onPress={() => setDevCalleeModalOpen(false)}
            hitSlop={12}
          >
            <Text style={{ color: "#fff", fontSize: 16, fontWeight: "600" }}>
              Close
            </Text>
          </Pressable>
        </View>
        {devCalleeModalOpen ? <DevMockCalleeScreen /> : null}
      </View>
    </Modal>
  );

  return (
    <>
      {legalSheetModal}
      {deleteAccountModal}
      {topUpSheetModal}
      {testCallModal}
      {devCalleeModal}
      {user && RECORDING_ENABLED ? (
        <CallRecordingsModal
          visible={recordingsTarget !== null}
          userId={user.id}
          callSessionId={recordingsTarget?.callSessionId ?? null}
          destinationNumber={recordingsTarget?.destinationNumber ?? null}
          onClose={() => setRecordingsTarget(null)}
        />
      ) : null}
      <SafeAreaView style={styles.safeArea}>
        <StatusBar style="light" />
        <View style={styles.backdropOrbTop} />
        <View style={styles.backdropOrbBottom} />
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          style={styles.keyboardFrame}
        >
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
          <TranslationDemoCard
            callerLanguage={callerLanguage}
            calleeLanguage={calleeLanguage}
            copy={copy}
          />
          {!user ? (
            <View style={styles.card}>
              <Text style={styles.cardTitle}>{copy.nativeLanguageSetupLabel}</Text>
              {renderLanguagePicker(nativeLanguage, setNativeLanguage, supportedCallerLanguages)}
              <Text style={styles.metaText}>{copy.nativeLanguageSetupHint}</Text>

              <Text style={styles.fieldLabel}>{copy.appLanguageLabel}</Text>
              <View style={styles.localeRow}>
                {(["en", "zh"] as AppLocale[]).map((option) => (
                  <Pressable
                    key={option}
                    onPress={() => setSelectedLocale(option)}
                    style={[
                      styles.localeButton,
                      selectedLocale === option && styles.localeButtonActive,
                    ]}
                  >
                    <Text
                      style={[
                        styles.localeButtonText,
                        selectedLocale === option && styles.localeButtonTextActive,
                      ]}
                    >
                      {option === "en" ? "English" : "繁體中文"}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>
          ) : null}

          <View style={styles.heroShell}>
            <View style={styles.hero}>
              <Text style={styles.eyebrow}>{copy.onboardingKicker}</Text>
              <Text style={styles.heroTitle}>{copy.appName}</Text>
              <Text style={styles.heroSubtitle}>{copy.onboardingTitle}</Text>
              <Text style={styles.heroBody}>{copy.onboardingBody}</Text>
            </View>
          </View>

          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          {isBusy ? <ActivityIndicator color="#16302b" size="small" /> : null}

            {!user ? (
              <View style={styles.card}>
                <Text style={styles.cardTitle}>{copy.howItWorksTitle}</Text>
                <View style={styles.onboardingStepList}>
                  {renderHowItWorksStep(
                    "01",
                    copy.howItWorksStepOneTitle,
                    copy.howItWorksStepOneBody,
                  )}
                  {renderHowItWorksStep(
                    "02",
                    copy.howItWorksStepTwoTitle,
                    copy.howItWorksStepTwoBody,
                  )}
                  {renderHowItWorksStep(
                    "03",
                    copy.howItWorksStepThreeTitle,
                    copy.howItWorksStepThreeBody,
                  )}
                </View>
              </View>
            ) : null}

            <View style={styles.card}>
              {!user ? (
                <>
                  <Text style={styles.cardTitle}>{copy.onboardingTitle}</Text>
                  <Text style={styles.cardBody}>{copy.onboardingBody}</Text>
                  <Text
                    style={{
                      color: "#14231f",
                      fontWeight: "600",
                      marginTop: 12,
                      marginBottom: 6,
                    }}
                  >
                    {copy.nameLabel}
                  </Text>
                  <TextInput
                    value={signupName}
                    onChangeText={setSignupName}
                    placeholder={copy.nameLabel}
                    placeholderTextColor="#7c8a86"
                    maxLength={80}
                    autoCorrect={false}
                    autoCapitalize="words"
                    style={{
                      borderWidth: 1,
                      borderColor: "#c9bda7",
                      borderRadius: 10,
                      padding: 12,
                      backgroundColor: "#fffaf3",
                      color: "#14231f",
                      marginBottom: 12,
                    }}
                  />
                  <View style={styles.legalActionRow}>
                    {renderLegalAction(
                      copy.privacyPolicyLabel,
                      "privacy",
                      legalLinks.privacyUrl,
                    )}
                    {renderLegalAction(
                      copy.termsLabel,
                      "terms",
                      legalLinks.termsUrl,
                    )}
                  </View>
                  {renderChecklistRow(
                    acceptedLegal,
                    () => setAcceptedLegal((current) => !current),
                    copy.legalConsentLabel,
                  )}
                  <Pressable onPress={handleSignup} style={styles.primaryButton}>
                    <Text style={styles.primaryButtonText}>{copy.continueCta}</Text>
                  </Pressable>
                </>
              ) : (
                <>
                  <Text style={styles.cardTitle}>{copy.walletTitle}</Text>
                  <Text style={styles.cardBody}>{copy.walletBody}</Text>
                  <View style={styles.metricRow}>
                    <View style={styles.metricPill}>
                      <Text style={styles.metricLabel}>{copy.creditLabel}</Text>
                      <Text style={styles.metricValue}>
                        {creditsToMinutes(wallet?.balanceCredits ?? 0, wallet?.ratePerMinuteCredits)} {copy.minutesUnit}
                      </Text>
                    </View>
                    <View style={styles.metricPill}>
                      <Text style={styles.metricLabel}>{copy.reserveLabel}</Text>
                      <Text style={styles.metricValue}>
                        {creditsToMinutes(minimumCallReserveCredits, wallet?.ratePerMinuteCredits)} {copy.minutesUnit}
                      </Text>
                    </View>
                  </View>
                  <Pressable onPress={handleTopUp} style={styles.secondaryButton}>
                    <Text style={styles.secondaryButtonText}>{copy.topUpCta}</Text>
                  </Pressable>
                </>
              )}
            </View>

            {user ? (
              <View style={styles.card}>
                <Text style={styles.cardTitle}>{copy.dialerTitle}</Text>

                <Text style={styles.fieldLabel}>{copy.destinationLabel}</Text>
                <TextInput
                  value={destinationNumber}
                  onChangeText={setDestinationNumber}
                  style={styles.input}
                  keyboardType="phone-pad"
                  autoCapitalize="none"
                  placeholder="+81312345678"
                  placeholderTextColor="#7c8684"
                />

                <Text style={styles.fieldLabel}>{copy.callerLanguageLabel}</Text>
                {renderLanguagePicker(callerLanguage, setCallerLanguage, supportedCallerLanguages)}

                <Text style={styles.fieldLabel}>{copy.calleeLanguageLabel}</Text>
                {renderReadOnlyLanguage(calleeLanguage)}

                <Text style={styles.metaText}>
                  {copy.readyToCall} • {creditsToMinutes(wallet?.balanceCredits ?? 0, wallet?.ratePerMinuteCredits)} {copy.minutesUnit}
                </Text>
                {renderChecklistRow(
                  callConsentConfirmed,
                  () => setCallConsentConfirmed((current) => !current),
                  copy.callConsentLabel,
                )}
                {renderChecklistRow(
                  acceptableUseConfirmed,
                  () => setAcceptableUseConfirmed((current) => !current),
                  copy.acceptableUseLabel,
                )}
                <Text style={styles.warningText}>{copy.emergencyNotice}</Text>
                <Text style={styles.metaText}>{copy.shortCodeBlockedNotice}</Text>
                {(wallet?.balanceCredits ?? 0) < minimumCallReserveCredits ? (
                  <Text style={styles.warningText}>{copy.balanceTooLow}</Text>
                ) : null}
                <Pressable
                  onPress={handleStartCall}
                  style={[
                    styles.primaryButton,
                    styles.callActionButton,
                    (wallet?.balanceCredits ?? 0) < minimumCallReserveCredits &&
                      styles.disabledButton,
                  ]}
                  disabled={
                    isBusy ||
                    !wallet ||
                    wallet.balanceCredits < minimumCallReserveCredits
                  }
                >
                  <Text style={styles.primaryButtonText}>{copy.startCallCta}</Text>
                </Pressable>
              </View>
            ) : null}

            {user ? (
              <View style={styles.card}>
                <Text style={styles.cardTitle}>{copy.legalLinksTitle}</Text>
                <Text style={styles.cardBody}>{copy.deleteAccountBody}</Text>
                <View style={styles.legalActionColumn}>
                  {renderLegalAction(
                    copy.privacyPolicyLabel,
                    "privacy",
                    legalLinks.privacyUrl,
                  )}
                  {renderLegalAction(copy.termsLabel, "terms", legalLinks.termsUrl)}
                  {renderLegalAction(copy.supportLabel, "support", legalLinks.supportUrl)}
                </View>
                <Text style={styles.metaText}>
                  {copy.supportContactLabel}: {legalLinks.supportEmail}
                </Text>
                <Pressable
                  onPress={() => setShowDeleteAccountConfirm(true)}
                  style={styles.dangerButton}
                >
                  <Text style={styles.dangerButtonText}>{copy.deleteAccountLabel}</Text>
                </Pressable>
              </View>
            ) : null}

            <View style={styles.card}>
              <Pressable
                onPress={() => setHistoryCollapsed((v) => !v)}
                style={styles.historyHeader}
              >
                <Text style={styles.cardTitle}>
                  {copy.callHistory}
                  {history.length > 0 ? ` (${Math.min(history.length, 5)})` : ""}
                </Text>
                <Text style={styles.historyChevron}>
                  {historyCollapsed ? "▸" : "▾"}
                </Text>
              </Pressable>
              {historyCollapsed ? null : history.length === 0 ? (
                <Text style={styles.cardBody}>{copy.noHistory}</Text>
              ) : (
                history.slice(0, 5).map((session) => {
                  const isExpanded = expandedHistory.has(session.id);
                  const toggleExpanded = () =>
                    setExpandedHistory((prev) => {
                      const next = new Set(prev);
                      if (next.has(session.id)) next.delete(session.id);
                      else next.add(session.id);
                      return next;
                    });
                  return (
                    <View key={session.id} style={styles.historyItem}>
                      <Pressable
                        onPress={toggleExpanded}
                        style={styles.historyHeader}
                      >
                        <Text style={styles.historyTitle}>
                          {session.destinationNumber}
                        </Text>
                        <Text style={styles.historyChevron}>
                          {isExpanded ? "▾" : "▸"}
                        </Text>
                      </Pressable>
                      <Text style={styles.metaText}>
                        {session.callerLanguage.toUpperCase()} →{" "}
                        {session.calleeLanguage.toUpperCase()} •{" "}
                        {((session.usedCredits ?? 0) / (session.estimatedRatePerMinuteCredits || wallet?.ratePerMinuteCredits || defaultRatePerMinute)).toFixed(1)}{" "}
                        {copy.minutesUsedLabel}
                      </Text>
                      {isExpanded ? (
                        <>
                          {session.providerCallStatus ? (
                            <Text style={styles.metaText}>
                              {copy.providerStatusLabel}: {session.providerCallStatus}
                            </Text>
                          ) : null}
                          {renderTranscript(session.transcript.slice(0, 2))}
                          {RECORDING_ENABLED ? (
                            <Pressable
                              onPress={() =>
                                setRecordingsTarget({
                                  callSessionId: session.id,
                                  destinationNumber: session.destinationNumber,
                                })
                              }
                              style={{
                                marginTop: 8,
                                alignSelf: "flex-start",
                                paddingVertical: 8,
                                paddingHorizontal: 12,
                                borderRadius: 8,
                                backgroundColor: "#16302b",
                              }}
                            >
                              <Text style={{ color: "#fff", fontWeight: "600" }}>
                                Open recordings
                              </Text>
                            </Pressable>
                          ) : null}
                        </>
                      ) : null}
                    </View>
                  );
                })
              )}
            </View>

            <Pressable
              onLongPress={toggleTestMode}
              delayLongPress={800}
              hitSlop={16}
            >
              <Text style={styles.buildInfoText}>{buildInfoLabel}</Text>
            </Pressable>
            {testModeEnabled ? (
              <View style={styles.devMockBanner}>
                <Text style={styles.devMockTitle}>
                  TEST MODE — calls skip Twilio (no charge)
                </Text>
                <Text style={styles.devMockHint}>
                  Start a test call on this device. Both caller and callee legs
                  open at once; switch tabs to change which role owns your mic.
                </Text>
                <Pressable
                  onPress={() => {
                    if (!user) {
                      Alert.alert("Sign in first", "Test call needs a user.");
                      return;
                    }
                    setTestCallModalOpen(true);
                  }}
                  style={styles.devMockButton}
                >
                  <Text style={styles.devMockButtonText}>Start test call</Text>
                </Pressable>
                <Pressable
                  onPress={() => setDevCalleeModalOpen(true)}
                  style={styles.devMockButtonGhost}
                >
                  <Text style={styles.devMockButtonGhostText}>
                    Advanced: Join as callee (2nd device)
                  </Text>
                </Pressable>
                <Pressable onPress={toggleTestMode} style={styles.devMockButtonGhost}>
                  <Text style={styles.devMockButtonGhostText}>
                    Turn off test mode
                  </Text>
                </Pressable>
              </View>
            ) : null}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#f4efe7",
  },
  callSafeArea: {
    flex: 1,
    backgroundColor: "#09111c",
  },
  callBackdropTop: {
    position: "absolute",
    top: -120,
    right: -20,
    width: 260,
    height: 260,
    borderRadius: 999,
    backgroundColor: "rgba(53, 88, 147, 0.44)",
  },
  callBackdropBottom: {
    position: "absolute",
    bottom: -120,
    left: -60,
    width: 280,
    height: 280,
    borderRadius: 999,
    backgroundColor: "rgba(26, 131, 113, 0.22)",
  },
  callShell: {
    flex: 1,
    paddingHorizontal: 18,
    paddingTop: 10,
    paddingBottom: 24,
    justifyContent: "space-between",
    gap: 14,
  },
  callTopBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  callStatusPill: {
    borderRadius: 999,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.08)",
  },
  callStatusPillText: {
    color: "#f2f6fd",
    fontSize: 13,
    fontWeight: "700",
  },
  callDurationText: {
    color: "#f7f3ed",
    fontSize: 20,
    fontWeight: "700",
  },
  callBody: {
    flex: 1,
    justifyContent: "space-between",
    gap: 14,
  },
  callHeroPanel: {
    alignItems: "center",
    gap: 8,
    paddingTop: 2,
  },
  callAvatarHalo: {
    position: "absolute",
    top: -12,
    width: 168,
    height: 168,
    borderRadius: 999,
    backgroundColor: "rgba(116, 161, 255, 0.12)",
    borderWidth: 1,
    borderColor: "rgba(208, 225, 255, 0.14)",
  },
  callAvatarPulse: {
    position: "absolute",
    top: -12,
    width: 168,
    height: 168,
    borderRadius: 999,
    borderWidth: 3,
    borderColor: "#7aa8ff",
    backgroundColor: "rgba(122, 168, 255, 0.18)",
  },
  callPrePickupStatus: {
    color: "#7aa8ff",
    fontSize: 18,
    fontWeight: "600",
    letterSpacing: 1,
    marginTop: 6,
    textTransform: "uppercase",
  },
  callAvatar: {
    width: 112,
    height: 112,
    borderRadius: 999,
    backgroundColor: "#f3e7d6",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.08)",
    shadowColor: "#000814",
    shadowOpacity: 0.28,
    shadowRadius: 22,
    shadowOffset: { width: 0, height: 18 },
  },
  callAvatarText: {
    color: "#0c1828",
    fontSize: 34,
    fontWeight: "800",
    letterSpacing: 2,
  },
  callRemoteNumber: {
    color: "#f8f6f2",
    fontSize: 26,
    fontWeight: "800",
    textAlign: "center",
    letterSpacing: 0.2,
  },
  callSecondaryText: {
    color: "rgba(234, 242, 255, 0.82)",
    fontSize: 15,
    fontWeight: "600",
  },
  callHintText: {
    color: "rgba(237, 243, 255, 0.8)",
    fontSize: 13,
    lineHeight: 19,
    textAlign: "center",
    paddingHorizontal: 18,
    maxWidth: 320,
  },
  callWarningBanner: {
    borderRadius: 18,
    backgroundColor: "rgba(220, 128, 61, 0.18)",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: "rgba(243, 170, 74, 0.3)",
  },
  callWarningText: {
    color: "#ffd7ad",
    fontSize: 13,
    lineHeight: 19,
    fontWeight: "700",
    textAlign: "center",
  },
  callInlineError: {
    color: "#fecaca",
    fontSize: 13,
    fontWeight: "700",
    textAlign: "center",
  },
  phoneControlGrid: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 16,
  },
  phoneCircleButton: {
    width: 88,
    height: 88,
    borderRadius: 999,
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
    gap: 2,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.08)",
  },
  phoneCircleButtonLight: {
    backgroundColor: "#f3e7d6",
  },
  phoneCircleButtonAccent: {
    backgroundColor: "#d4f1cf",
  },
  phoneCircleButtonInfo: {
    width: 88,
    height: 88,
    borderRadius: 999,
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
    gap: 2,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.05)",
  },
  phoneCircleButtonLabel: {
    color: "#f8f6f2",
    fontSize: 14,
    fontWeight: "800",
    textAlign: "center",
  },
  phoneCircleButtonLabelDark: {
    color: "#0f172a",
  },
  phoneCircleButtonState: {
    color: "rgba(247, 243, 237, 0.78)",
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 0.6,
    textAlign: "center",
  },
  phoneCircleButtonStateDark: {
    color: "#334155",
  },
  callTranscriptCollapsed: {
    borderRadius: 24,
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    paddingHorizontal: 18,
    paddingVertical: 16,
    gap: 6,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.06)",
  },
  callTranscriptCollapsedTitle: {
    color: "rgba(240, 245, 255, 0.72)",
    fontSize: 12,
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  callTranscriptCollapsedBody: {
    color: "#f2f6fd",
    fontSize: 14,
    lineHeight: 20,
  },
  callTranscriptPanel: {
    maxHeight: 200,
    borderRadius: 28,
    backgroundColor: "rgba(247, 242, 235, 0.97)",
    paddingTop: 12,
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 12,
  },
  callTranscriptPanelHandle: {
    alignSelf: "center",
    width: 42,
    height: 5,
    borderRadius: 999,
    backgroundColor: "#d2c7bb",
  },
  callTranscriptHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  callTranscriptTitle: {
    color: "#101b28",
    fontSize: 18,
    fontWeight: "800",
  },
  callTranscriptMeta: {
    color: "#5b6472",
    fontSize: 13,
    fontWeight: "700",
  },
  callTranscriptScroll: {
    flex: 1,
  },
  callTranscriptContent: {
    gap: 10,
    paddingBottom: 6,
  },
  callTranscriptEmpty: {
    color: "#657181",
    fontSize: 14,
    lineHeight: 20,
  },
  callTranscriptCard: {
    borderRadius: 20,
    backgroundColor: "#fffdf9",
    padding: 14,
    gap: 6,
    borderWidth: 1,
    borderColor: "#ece3d8",
  },
  callTranscriptSpeaker: {
    color: "#677487",
    fontSize: 11,
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: 0.9,
  },
  callTranscriptSource: {
    color: "#122033",
    fontSize: 15,
    lineHeight: 21,
    fontWeight: "700",
  },
  callTranscriptTranslated: {
    color: "#4d5c72",
    fontSize: 14,
    lineHeight: 20,
  },
  phoneEndButton: {
    alignSelf: "center",
    width: 152,
    height: 60,
    borderRadius: 999,
    backgroundColor: "#d64b43",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 20,
    shadowColor: "#4c0d0d",
    shadowOpacity: 0.26,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 12 },
  },
  phoneEndButtonText: {
    color: "#fff8f5",
    fontSize: 16,
    fontWeight: "800",
    textAlign: "center",
  },
  callSpinner: {
    marginTop: -6,
  },
  keyboardFrame: {
    flex: 1,
  },
  backdropOrbTop: {
    position: "absolute",
    top: -80,
    right: -30,
    width: 220,
    height: 220,
    borderRadius: 999,
    backgroundColor: "#d7ead8",
    opacity: 0.9,
  },
  backdropOrbBottom: {
    position: "absolute",
    bottom: 120,
    left: -60,
    width: 180,
    height: 180,
    borderRadius: 999,
    backgroundColor: "#f3dcc7",
    opacity: 0.8,
  },
  scrollContent: {
    paddingHorizontal: 18,
    paddingTop: 14,
    paddingBottom: 44,
    gap: 16,
  },
  heroShell: {
    position: "relative",
    marginBottom: 2,
  },
  hero: {
    backgroundColor: "#16302b",
    borderRadius: 30,
    paddingHorizontal: 22,
    paddingTop: 22,
    paddingBottom: 28,
    gap: 9,
    shadowColor: "#16302b",
    shadowOpacity: 0.18,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 16 },
  },
  eyebrow: {
    color: "#d7f2c6",
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 1.1,
    textTransform: "uppercase",
  },
  heroTitle: {
    color: "#fff8ee",
    fontSize: 31,
    fontWeight: "800",
  },
  heroSubtitle: {
    color: "#fff8ee",
    fontSize: 26,
    fontWeight: "700",
    lineHeight: 32,
  },
  heroBody: {
    color: "#dce7df",
    fontSize: 15,
    lineHeight: 22,
  },
  card: {
    backgroundColor: "rgba(255, 250, 241, 0.94)",
    borderRadius: 28,
    padding: 18,
    gap: 12,
    borderWidth: 1,
    borderColor: "#e3d7c7",
    shadowColor: "#8f826c",
    shadowOpacity: 0.08,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
  },
  cardTitle: {
    color: "#0f172a",
    fontSize: 21,
    fontWeight: "800",
  },
  cardBody: {
    color: "#4c5c59",
    fontSize: 15,
    lineHeight: 22,
  },
  onboardingStepList: {
    gap: 12,
  },
  onboardingStepCard: {
    flexDirection: "row",
    gap: 12,
    alignItems: "flex-start",
    borderRadius: 22,
    backgroundColor: "#f6eee2",
    borderWidth: 1,
    borderColor: "#eadfce",
    paddingHorizontal: 14,
    paddingVertical: 14,
  },
  onboardingStepBadge: {
    width: 40,
    height: 40,
    borderRadius: 999,
    backgroundColor: "#16302b",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  onboardingStepBadgeText: {
    color: "#fff8ee",
    fontSize: 12,
    fontWeight: "800",
    letterSpacing: 0.8,
  },
  onboardingStepCopy: {
    flex: 1,
    gap: 4,
  },
  onboardingStepTitle: {
    color: "#102320",
    fontSize: 15,
    fontWeight: "800",
  },
  onboardingStepBody: {
    color: "#52615e",
    fontSize: 14,
    lineHeight: 20,
  },
  localeRow: {
    flexDirection: "row",
    gap: 8,
  },
  localeButton: {
    flex: 1,
    borderRadius: 999,
    minHeight: 48,
    paddingVertical: 12,
    paddingHorizontal: 14,
    backgroundColor: "#ece4d8",
    justifyContent: "center",
  },
  localeButtonActive: {
    backgroundColor: "#16302b",
  },
  localeButtonText: {
    color: "#1f2937",
    textAlign: "center",
    fontWeight: "700",
  },
  localeButtonTextActive: {
    color: "#fff8ee",
  },
  fieldLabel: {
    color: "#3d4c49",
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 1.15,
  },
  input: {
    borderRadius: 20,
    backgroundColor: "#ece4d8",
    paddingHorizontal: 16,
    paddingVertical: 15,
    color: "#0f172a",
    fontSize: 16,
    borderWidth: 1,
    borderColor: "#e6dccf",
  },
  checklistRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  checklistBox: {
    width: 22,
    height: 22,
    borderRadius: 7,
    borderWidth: 1.5,
    borderColor: "#9aa7a4",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
    backgroundColor: "#fff9f0",
  },
  checklistBoxChecked: {
    backgroundColor: "#16302b",
    borderColor: "#16302b",
  },
  checklistBoxInner: {
    width: 8,
    height: 8,
    borderRadius: 999,
    backgroundColor: "#fff8ee",
  },
  checklistLabel: {
    flex: 1,
    color: "#31403d",
    fontSize: 14,
    lineHeight: 21,
    fontWeight: "600",
  },
  legalActionRow: {
    flexDirection: "row",
    gap: 10,
  },
  legalActionColumn: {
    gap: 10,
  },
  legalActionButton: {
    flex: 1,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "#e4d7c5",
    backgroundColor: "#f8f1e6",
    paddingHorizontal: 14,
    paddingVertical: 14,
    gap: 4,
  },
  legalActionText: {
    color: "#132421",
    fontSize: 14,
    fontWeight: "800",
  },
  legalActionMeta: {
    color: "#64726f",
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  segmentRow: {
    flexDirection: "row",
    gap: 8,
  },
  segmentButton: {
    flex: 1,
    borderRadius: 999,
    backgroundColor: "#ece4d8",
    minHeight: 46,
    paddingVertical: 10,
    paddingHorizontal: 8,
    justifyContent: "center",
  },
  segmentButtonActive: {
    backgroundColor: "#c96c3d",
  },
  segmentButtonText: {
    color: "#31403d",
    textAlign: "center",
    fontWeight: "700",
  },
  segmentButtonTextActive: {
    color: "#fffaf1",
  },
  primaryButton: {
    borderRadius: 999,
    backgroundColor: "#16302b",
    minHeight: 54,
    paddingVertical: 15,
    paddingHorizontal: 18,
    justifyContent: "center",
  },
  primaryButtonText: {
    color: "#fff8ee",
    fontSize: 16,
    fontWeight: "800",
    textAlign: "center",
  },
  secondaryButton: {
    borderRadius: 999,
    backgroundColor: "#d7f2c6",
    minHeight: 52,
    paddingVertical: 14,
    paddingHorizontal: 18,
    justifyContent: "center",
  },
  secondaryButtonText: {
    color: "#16302b",
    fontWeight: "800",
    textAlign: "center",
  },
  dangerButton: {
    borderRadius: 999,
    backgroundColor: "#8f2c24",
    minHeight: 52,
    paddingVertical: 14,
    paddingHorizontal: 18,
    justifyContent: "center",
  },
  dangerButtonText: {
    color: "#fff5f3",
    fontWeight: "800",
    textAlign: "center",
  },
  disabledButton: {
    opacity: 0.4,
  },
  metricRow: {
    flexDirection: "row",
    gap: 12,
  },
  metricPill: {
    flex: 1,
    borderRadius: 22,
    backgroundColor: "#ece4d8",
    padding: 14,
    gap: 6,
  },
  metricLabel: {
    color: "#6a736e",
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  metricValue: {
    color: "#0f172a",
    fontSize: 24,
    fontWeight: "800",
  },
  metaText: {
    color: "#536461",
    fontSize: 14,
    lineHeight: 20,
  },
  statusLine: {
    color: "#233936",
    fontSize: 16,
    fontWeight: "700",
  },
  warningText: {
    color: "#9a3412",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
  },
  errorText: {
    color: "#991b1b",
    fontSize: 14,
    fontWeight: "700",
  },
  buildInfoText: {
    color: "#8a8579",
    fontSize: 11,
    fontWeight: "600",
    textAlign: "center",
    marginTop: 8,
    marginBottom: 4,
    letterSpacing: 0.3,
  },
  devMockBanner: {
    marginTop: 12,
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#2a1f0f",
    borderWidth: 1,
    borderColor: "#7a5a1a",
    gap: 6,
  },
  devMockTitle: {
    color: "#ffcb4b",
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 0.3,
  },
  devMockSessionId: {
    color: "#fff",
    fontSize: 12,
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
  },
  devMockHint: {
    color: "#c7b680",
    fontSize: 11,
    lineHeight: 16,
  },
  devMockButton: {
    marginTop: 8,
    backgroundColor: "#3478f6",
    paddingVertical: 10,
    borderRadius: 6,
    alignItems: "center",
  },
  devMockButtonText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },
  devMockButtonGhost: {
    marginTop: 4,
    paddingVertical: 8,
    alignItems: "center",
  },
  devMockButtonGhostText: {
    color: "#c7b680",
    fontSize: 12,
  },
  controlsRow: {
    flexDirection: "row",
    gap: 10,
  },
  controlChip: {
    borderRadius: 999,
    backgroundColor: "#ece4d8",
    minHeight: 46,
    paddingHorizontal: 16,
    paddingVertical: 10,
    justifyContent: "center",
  },
  controlChipActive: {
    backgroundColor: "#16302b",
  },
  controlChipActiveSecondary: {
    backgroundColor: "#c96c3d",
  },
  controlChipText: {
    color: "#233936",
    fontWeight: "700",
  },
  controlChipTextActive: {
    color: "#fff8ee",
  },
  controlChipTextActiveSecondary: {
    color: "#fff8ee",
  },
  transcriptList: {
    gap: 10,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(7, 14, 23, 0.5)",
    justifyContent: "flex-end",
    padding: 14,
  },
  modalSheet: {
    maxHeight: "82%",
    borderRadius: 28,
    backgroundColor: "#fff8ee",
    paddingTop: 18,
    paddingHorizontal: 18,
    paddingBottom: 18,
    gap: 12,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  modalTitle: {
    flex: 1,
    color: "#132421",
    fontSize: 22,
    fontWeight: "800",
  },
  modalHeaderButton: {
    borderRadius: 999,
    backgroundColor: "#ece4d8",
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  modalHeaderButtonText: {
    color: "#233936",
    fontWeight: "800",
  },
  modalScroll: {
    flexGrow: 0,
  },
  modalContent: {
    gap: 16,
    paddingBottom: 4,
  },
  modalIntro: {
    color: "#425451",
    fontSize: 15,
    lineHeight: 22,
  },
  modalSection: {
    gap: 6,
  },
  modalSectionHeading: {
    color: "#132421",
    fontSize: 15,
    fontWeight: "800",
  },
  modalSectionBody: {
    color: "#4f5f5b",
    fontSize: 14,
    lineHeight: 21,
  },
  modalFooter: {
    paddingTop: 4,
  },
  modalSecondaryButton: {
    borderRadius: 999,
    backgroundColor: "#ece4d8",
    minHeight: 50,
    paddingHorizontal: 18,
    paddingVertical: 14,
    justifyContent: "center",
  },
  modalSecondaryButtonText: {
    color: "#233936",
    fontWeight: "800",
    textAlign: "center",
  },
  confirmSheet: {
    borderRadius: 28,
    backgroundColor: "#fff8ee",
    padding: 20,
    gap: 16,
  },
  confirmTitle: {
    color: "#7f1d1d",
    fontSize: 22,
    fontWeight: "800",
  },
  confirmBody: {
    color: "#4b5563",
    fontSize: 15,
    lineHeight: 22,
  },
  confirmActions: {
    gap: 10,
  },
  transcriptCard: {
    borderRadius: 20,
    backgroundColor: "#f3ece0",
    padding: 14,
    gap: 6,
  },
  transcriptSpeaker: {
    color: "#6b7280",
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  transcriptSource: {
    color: "#15211f",
    fontSize: 15,
    lineHeight: 21,
    fontWeight: "700",
  },
  transcriptTranslated: {
    color: "#475569",
    fontSize: 14,
    lineHeight: 20,
  },
  callCard: {
    backgroundColor: "#f7efe3",
  },
  historyItem: {
    borderTopWidth: 1,
    borderTopColor: "#e6dac7",
    paddingTop: 14,
    gap: 8,
  },
  historyTitle: {
    color: "#132421",
    fontSize: 17,
    fontWeight: "800",
  },
  historyHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  historyChevron: {
    color: "#5a6b67",
    fontSize: 18,
    paddingHorizontal: 6,
  },
  callActionButton: {
    marginTop: 4,
  },
});
